/**
 * Variable Profit Analysis Card for Accountant Report View
 * Displays the 4-step VP calculation breakdown with methodology
 */

import React from 'react';
import CalculationMethodology from '../CalculationMethodology';

interface VariableProfitAnalysis {
  variable_margin_pct?: number;
  benchmark_vp?: number;
  coverage_vp?: number;
  vp_loss?: number;
  coverage_revenue?: number;
}

interface BenchmarkPeriod {
  revenue?: number;
  variable_profit?: number;
}

interface VariableProfitAnalysisCardProps {
  variableProfitAnalysis: VariableProfitAnalysis;
  benchmarkPeriod: BenchmarkPeriod;
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function VariableProfitAnalysisCard({
  variableProfitAnalysis,
  benchmarkPeriod,
  formatCurrency,
  formatPercent
}: VariableProfitAnalysisCardProps) {
  if (!variableProfitAnalysis) return null;

  // Read the authoritative coverage-period revenue from the backend engine
  // (P0-3 — no longer derived by dividing coverage_vp by margin).
  const calculateCoverageRevenue = () => {
    return variableProfitAnalysis.coverage_revenue;
  };

  const coverageRevenue = calculateCoverageRevenue();

  // Hue law (K3 review 2026-08-23): the four step cards are STRUCTURE — tinting
  // steps 1 & 4 orange is decoration, and orange never renders data. All four
  // read identical neutral; eyebrow + value + formula chip all hueless DM Mono.
  // Step 4 (the RESULT) keeps a single 2px orange LEFT border as its only accent.
  const cardClass = 'rounded-[2px] p-4 border border-l-2';
  const neutralCard: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-elevated)',
    borderColor: 'var(--color-border)',
    borderLeftColor: 'var(--color-border)',
  };
  const resultCard: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-elevated)',
    borderColor: 'var(--color-border)',
    borderLeftColor: 'var(--color-brand)',
  };
  const eyebrow = 'text-[11px] uppercase tracking-wide mb-1 font-mono';
  const eyebrowStyle: React.CSSProperties = { color: 'var(--color-text-muted)' };
  const stepLabel = 'text-sm mb-2 font-semibold';
  const stepLabelStyle: React.CSSProperties = { color: 'var(--color-text-secondary)' };
  const valueClass = 'text-2xl font-mono font-semibold mb-2';
  const valueStyle: React.CSSProperties = { color: 'var(--color-text-secondary)' };
  const chipClass = 'text-xs rounded-[2px] px-2 py-1 font-mono';
  const chipStyle: React.CSSProperties = { color: 'var(--color-text-muted)', backgroundColor: 'transparent' };

  return (
    <div className="rounded-[2px] border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h2 className="text-xl font-serif mb-4" style={{ color: 'var(--color-text-primary)', fontWeight: 400 }}>
        Variable Profit Analysis
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Step 1: Variable Margin */}
        <div className={cardClass} style={neutralCard}>
          <p className={eyebrow} style={eyebrowStyle}>STEP 1</p>
          <p className={stepLabel} style={stepLabelStyle}>Variable Margin</p>
          <p className={valueClass} style={valueStyle}>
            {formatPercent(variableProfitAnalysis.variable_margin_pct)}
          </p>
          <div className={chipClass} style={chipStyle}>
            {formatCurrency(benchmarkPeriod?.variable_profit)} ÷ {formatCurrency(benchmarkPeriod?.revenue)}
          </div>
        </div>

        {/* Step 2: Benchmark VP */}
        <div className={cardClass} style={neutralCard}>
          <p className={eyebrow} style={eyebrowStyle}>STEP 2</p>
          <p className={stepLabel} style={stepLabelStyle}>Benchmark VP</p>
          <p className={valueClass} style={valueStyle}>
            {formatCurrency(variableProfitAnalysis.benchmark_vp)}
          </p>
          <div className={chipClass} style={chipStyle}>
            {formatCurrency(benchmarkPeriod?.revenue)} × {formatPercent(variableProfitAnalysis.variable_margin_pct)}
          </div>
        </div>

        {/* Step 3: Coverage VP */}
        <div className={cardClass} style={neutralCard}>
          <p className={eyebrow} style={eyebrowStyle}>STEP 3</p>
          <p className={stepLabel} style={stepLabelStyle}>Coverage VP</p>
          <p className={valueClass} style={valueStyle}>
            {formatCurrency(variableProfitAnalysis.coverage_vp)}
          </p>
          <div className={chipClass} style={chipStyle}>
            {coverageRevenue ? `${formatCurrency(coverageRevenue)} × ${formatPercent(variableProfitAnalysis.variable_margin_pct)}` : 'N/A'}
          </div>
        </div>

        {/* Step 4: VP Loss — the RESULT card carries the single orange left accent. */}
        <div className={cardClass} style={resultCard}>
          <p className={eyebrow} style={eyebrowStyle}>STEP 4: RESULT</p>
          <p className={stepLabel} style={stepLabelStyle}>VP Loss</p>
          <p className={valueClass} style={valueStyle}>
            {formatCurrency(variableProfitAnalysis.vp_loss)}
          </p>
          <div className={chipClass} style={chipStyle}>
            {formatCurrency(variableProfitAnalysis.benchmark_vp)} − {formatCurrency(variableProfitAnalysis.coverage_vp)}
          </div>
        </div>
      </div>
      
      {/* Methodology Explanation */}
      <CalculationMethodology 
        section="variable_profit" 
        data={{
          ...variableProfitAnalysis,
          benchmark_revenue: benchmarkPeriod?.revenue,
          benchmark_variable_expenses: benchmarkPeriod?.revenue && benchmarkPeriod?.variable_profit 
            ? benchmarkPeriod.revenue - benchmarkPeriod.variable_profit 
            : undefined,
          coverage_revenue: coverageRevenue,
          coverage_variable_expenses: coverageRevenue && variableProfitAnalysis.coverage_vp
            ? coverageRevenue - variableProfitAnalysis.coverage_vp
            : undefined
        }} 
      />
    </div>
  );
}
